/* eslint-disable import/no-anonymous-default-export */
import { HubConnectionBuilder } from '@microsoft/signalr';

let URL = process.env.REACT_APP_JOBS_API_URL;

export interface JobUpdateJobViewModel {
  id?: string;
  name?: string;
  progress?: number;
  state?: string;
  finishDate?: string;
}

export interface JobUpdateViewModel {
  runId: string;
  exportJob?: JobUpdateJobViewModel;
  saveJob?: JobUpdateJobViewModel;
}

export type RunHandler = (run: JobUpdateViewModel) => void;

export type RunProgressType = {
  runs: Record<string, any>; 
  handlers: Record<string, RunHandler[]>;
  setRun: (runId: string, run: JobUpdateViewModel) => void; 
  addHandler: (runId: string, handler: RunHandler) => void;
  removeHandler: (runId: string) => void;
};

const sessionRunStates: RunProgressType = {
  runs: {},
  handlers: {},
  setRun(runId, run) {
    this.runs[runId] = run;

    if (this.handlers[runId]) {
      this.handlers[runId](run);
    }
  },
  addHandler(runId, handler) {
    this.handlers[runId] = handler;
  },
  removeHandler(runId) {
    delete this.handlers[runId]
  }
};
export { sessionRunStates };

export interface SessionJob {
  id?: string;
  name?: string;
  type?: string;
  key?: string;
  runId?: string;
  progress?: number;
  state?: string;
  finishState?: string;
  message?: string;
}

export interface SessionJobsUpdate {
  sessionId: string;
  jobs: SessionJob[];
}

export type SessionJobsHandler = (jobs: SessionJob[]) => void;

const sessionJobs: Record<string, SessionJob[]> = {};
const sessionJobHandlers: Record<string, Set<SessionJobsHandler>> = {};
const subscribedSessionIds: Set<string> = new Set();

const sessionJobStates = {
  jobs: sessionJobs,
  handlers: sessionJobHandlers,
  subscribedSessionIds,
  set(update: SessionJobsUpdate) {
    this.jobs[update.sessionId] = update.jobs;
    this.handlers[update.sessionId]?.forEach((handler) => handler(update.jobs));
  },
  addHandler(sessionId: string, handler: SessionJobsHandler) {
    this.handlers[sessionId] = this.handlers[sessionId] ?? new Set();
    this.handlers[sessionId].add(handler);

    return () => {
      this.handlers[sessionId]?.delete(handler);
    };
  },
};
export { sessionJobStates };

const subscribedRunIds: Set<string> = new Set();
let connecting: Promise<any> | null = null;

const resubscribe = (connection: any) => {
  const sessionIds = Array.from(sessionJobStates.subscribedSessionIds);
  const runIds = Array.from(subscribedRunIds);

  if (sessionIds.length > 0) {
    connection.invoke('SubscribeToSessions', sessionIds);
  }

  if (runIds.length > 0) {
    connection.invoke('SubscribeToJobs', runIds);
  }
};

const connect = async () => {
  const connection = new HubConnectionBuilder()
    .withUrl(`${URL}halston/jobs/feed`)
    .withAutomaticReconnect()
    .build();

  connection.on('OnJobUpdate', (r) => {
    sessionRunStates.setRun(r.runId, r);
  });

  connection.on('OnSessionJobsUpdate', (update: SessionJobsUpdate) => {
    sessionJobStates.set(update);
  });

  connection.onreconnected(() => resubscribe(connection));

  await connection.start();
  window.signalRConnection = connection;

  return connection;
};

export default {
  async getSignalRConnection() {
    connecting = connecting ?? connect();

    try
    {
      return await connecting;
    }
    catch (e) {
      console.log('Failed to connect to signalR', e);
      connecting = null;

      // swallow
      return null;
    }
  },
  async subscribeToSessions(sessionIds: string[]) {
    sessionIds.forEach((id) => sessionJobStates.subscribedSessionIds.add(id));

    try
    {
      const connection = await this.getSignalRConnection();

      await connection?.invoke('SubscribeToSessions', sessionIds);
    }
    catch (e) {
      // swallow
      console.log('Failed to subscribe to sessions', e);
    }
  },
  async subscribeToJob(runIds: string[]) {
    runIds.forEach((id) => subscribedRunIds.add(id));

    try
    {
      const connection = await this.getSignalRConnection();

      await connection?.invoke('SubscribeToJobs', runIds);
    }
    catch (e) {
      // swallow
      console.log('Failed to subscribe to jobs', e);
    }
  }
};
